import React, { useState, useCallback, useEffect } from "react";
import {
  Row,
  Col,
  Modal,
  Button,
  Tooltip,
  Badge,
  Tag,
  Timeline,
  Skeleton,
} from "antd";
import formatDistance from "date-fns/formatDistance";
import { it } from "date-fns/locale";
import "moment/locale/it";
import { useAuth0 } from "../../../react-auth0-spa";
import config from "../../../api_config.json";
import { useAppContext } from "../../../context";
import fromUnixTime from "date-fns/fromUnixTime";
import format from "date-fns/format";
import ReactJson from "@microlink/react-json-view";
import {
  CloseCircleOutlined,
  UserSwitchOutlined,
  SearchOutlined,
} from "@ant-design/icons";

const appColors = {
  ofc: "purple",
  eterno: "purple",
  giotto: "geekblue",
};

const actionColors = {
  startup: "lime",
  removebg: "lime",
  login: "lime",
  create: "green",
  update: "cyan",
  delete: "orange",
};

const now = new Date();
const PAGE_SIZE = 50;

const makeTitle = (item) => {
  return (
    <>
      {item.action && (
        <Tag color={item.error ? "volcano" : actionColors[item.action]}>
          {item.action.toUpperCase()}
          {item.objectClass ? `: ${item.objectClass}` : ""}
        </Tag>
      )}
      {item.timestamp && (
        <Tooltip
          title={
            item.timestamp
              ? format(fromUnixTime(item.timestamp), "dd/MM/yyyy - HH:mm") +
                " UTC"
              : ""
          }
        >
          <Tag>
            {formatDistance(fromUnixTime(item.timestamp), now, {
              locale: it,
            })}
          </Tag>
        </Tooltip>
      )}
    </>
  );
};

const OrgTimeline = ({ orgId }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();

  const [isModalVisible, setIsModalVisible] = useState({});

  const showModal = (index) => () => {
    setIsModalVisible((current) => ({ ...current, [index]: true }));
  };

  const handleCancel = (index) => () => {
    setIsModalVisible((current) => ({ ...current, [index]: false }));
  };

  const fetchTimelinePage = useCallback(
    async (cursor) => {
      const token = await getTokenSilently();
      const params = {
        orgId,
        pageSize: PAGE_SIZE,
        paginated: true,
      };

      if (cursor) {
        params.cursor = cursor;
      }

      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/events?` +
          new URLSearchParams(params),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Cannot fetch timeline events (${response.status})`);
      }

      const responseData = await response.json();

      if (Array.isArray(responseData)) {
        return {
          items: responseData,
          nextCursor: null,
          hasMore: false,
        };
      }

      return {
        items: responseData.items || [],
        nextCursor: responseData.nextCursor || null,
        hasMore: Boolean(responseData.hasMore && responseData.nextCursor),
      };
    },
    [getTokenSilently, isProd, orgId],
  );

  const refreshData = useCallback(async () => {
    setLoading(true);

    try {
      const data = await fetchTimelinePage();
      setItems(data.items);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
      setIsModalVisible({});
    } catch (error) {
      console.error(error);
      setItems([]);
      setNextCursor(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [fetchTimelinePage]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;

    setLoadingMore(true);

    try {
      const data = await fetchTimelinePage(nextCursor);
      setItems((currentItems) => [...currentItems, ...data.items]);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  if (loading) {
    return <Skeleton loading={loading} />;
  }

  const onCopy = (copy) => {
    const container = document.createElement("textarea");
    const val = copy.src;

    container.innerHTML =
      typeof val === "string" ? val : JSON.stringify(val, null, "  ");

    document.body.appendChild(container);
    container.select();
    // copy the same quote-less value back to the clipboard
    document.execCommand("copy");
    document.body.removeChild(container);
  };

  return (
    <>
      <Row gutter={9} style={{ marginTop: "20px" }}>
        <Col>
          <Button onClick={refreshData}>Aggiorna</Button>
        </Col>
      </Row>
      <Row gutter={9} style={{ marginTop: "20px" }}>
        <Col span={9}>
          <Timeline mode="left">
            {items.map((item, index) => {
              if (Object.keys(item).length === 0) {
                return "";
              }

              const timelineItem = {
                ...item,
                timestamp:
                  `${item.timestamp}`.length === 13
                    ? Math.floor(item.timestamp / 1000)
                    : item.timestamp,
              };

              let icon;

              if (timelineItem.error) {
                icon = <CloseCircleOutlined />;
              }

              if (timelineItem.action) {
                icon = <UserSwitchOutlined />;
              }

              const application = timelineItem.application || "System Event";

              return (
                <Timeline.Item
                  key={index}
                  color={
                    timelineItem.error
                      ? "red"
                      : actionColors[timelineItem.action]
                  }
                  dot={icon}
                  label={
                    <Badge dot={timelineItem.error} offset={[0, 10]}>
                      <Tag color={appColors[application]}>
                        {application.toUpperCase()}
                      </Tag>
                    </Badge>
                  }
                >
                  {makeTitle(timelineItem)}
                  <Button
                    onClick={showModal(index)}
                    size="small"
                    shape="circle"
                    icon={<SearchOutlined />}
                  />
                  <Modal
                    title="Event Details"
                    open={isModalVisible[index]}
                    footer={null}
                    onCancel={handleCancel(index)}
                  >
                    <ReactJson
                      src={timelineItem}
                      name={null}
                      enableClipboard={onCopy}
                    />
                  </Modal>
                </Timeline.Item>
              );
            })}
          </Timeline>
          {hasMore && (
            <Button
              block
              loading={loadingMore}
              onClick={loadMore}
              style={{ marginBottom: "20px" }}
            >
              Carica altri
            </Button>
          )}
        </Col>
      </Row>
    </>
  );
};

export default OrgTimeline;
